/**
 * PUBLIC route — /doa/sign?row_id=<rowId>[&signed=1]
 *
 * PIN-gated signing page for DoA delegation rows. Mirrors eConsent's
 * RemoteConsentPage pattern:
 *
 *   1. Staff receives ONE email at initiation: the link to this page.
 *      (2026-07-29 — the PIN is NO LONGER pre-sent. Mailing the code
 *      before the signer opened the document meant possession of the PIN
 *      proved nothing; eConsent already worked this way.)
 *   2. Staff opens link → clicks "Send PIN to my email" →
 *      POST /doa/rows/{id}/signing-otp mints and delivers the code.
 *   3. Staff enters PIN → POST /doa/rows/{id}/sign/verify-pin
 *      → returns embed_url → auto-redirects to DocuSeal signing page.
 *   4. DocuSeal redirects back to /doa/sign?row_id=<id>&signed=1 → success page.
 *
 * No JWT required — intentionally public route.
 */
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle, ClipboardSignature, ShieldCheck } from 'lucide-react';
import { doa } from '@/api/client';

const PIN_LEN = 6;

export function DoaSignPage() {
  const [params] = useSearchParams();
  const rowId = params.get('row_id') ?? '';

  const [digits, setDigits] = useState<string[]>(Array(PIN_LEN).fill(''));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [embedUrl, setEmbedUrl] = useState<string | null>(null);
  const [autoRedirect, setAutoRedirect] = useState(false);
  // PIN is minted on demand now, so the page has to offer that step.
  const [pinSent, setPinSent] = useState(false);
  const [sendingPin, setSendingPin] = useState(false);
  const [pinDestination, setPinDestination] = useState<string | null>(null);

  const pin = digits.join('');
  const ready = pin.length === PIN_LEN && /^\d+$/.test(pin) && !!rowId;

  async function requestPin() {
    if (!rowId || sendingPin) return;
    setSendingPin(true);
    setError(null);
    try {
      const r = await doa.startRowOtp(rowId);
      setPinSent(true);
      setPinDestination(r.destination_masked ?? null);
    } catch (e: unknown) {
      const err = e as { status?: number; message?: string };
      setError(
        err.status === 409
          ? 'This delegation has already been signed.'
          : err.message || 'Could not send the PIN. Contact the study coordinator.',
      );
    } finally {
      setSendingPin(false);
    }
  }

  async function submit() {
    if (!ready || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const r = await doa.verifyRowPin(rowId, { pin });
      if (!r.embed_url) {
        setError('Signing session not ready — contact the study coordinator.');
        setDigits(Array(PIN_LEN).fill(''));
        return;
      }
      setAutoRedirect(true);
      setEmbedUrl(r.embed_url);
    } catch (e: unknown) {
      const err = e as { status?: number; code?: string; message?: string };
      if (err.status === 409) {
        // already signed — show success
        setAutoRedirect(false);
        setEmbedUrl('__signed__');
        return;
      }
      if (err.code === 'PIN_EXPIRED') {
        setError('Your PIN has expired. Contact the study coordinator for a new one.');
      } else if (err.code === 'PIN_CONSUMED') {
        setError('Your PIN has already been used. If you have not finished signing, contact the coordinator.');
      } else if (err.code === 'PIN_NOT_FOUND') {
        setError('No active PIN found. Contact the study coordinator.');
      } else if (err.status === 423) {
        setError('Too many failed attempts. Contact the study coordinator for a new PIN.');
      } else {
        setError('Incorrect PIN. Please check the email and try again.');
      }
      setDigits(Array(PIN_LEN).fill(''));
    } finally {
      setSubmitting(false);
    }
  }

  // Auto-submit when the 6th digit lands.
  useEffect(() => {
    if (ready) submit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pin]);

  useEffect(() => {
    if (embedUrl && embedUrl !== '__signed__' && autoRedirect) {
      window.location.href = embedUrl;
    }
  }, [embedUrl, autoRedirect]);

  // ── already signed (returned from DocuSeal via ?signed=1 or 409) ──────────
  if (params.get('signed') === '1' || embedUrl === '__signed__') {
    return (
      <Shell>
        <CheckCircle className="mx-auto h-12 w-12 text-green-500" />
        <p className="mt-4 text-center text-base font-semibold text-ink-primary">
          Delegation accepted
        </p>
        <p className="mt-1 text-center text-xs text-ink-muted">
          Your signature has been recorded. You will receive a confirmation email
          with a link to view your signed document.
        </p>
        <p className="mt-4 text-center text-[11px] text-ink-muted">
          You may close this window.
        </p>
      </Shell>
    );
  }

  if (!rowId) {
    return (
      <Shell>
        <p className="text-sm text-ink-muted">
          This link is missing the signing reference. Open it exactly as it arrived in the email.
        </p>
      </Shell>
    );
  }

  // Redirecting — hide the form.
  if (embedUrl && autoRedirect) return null;

  return (
    <Shell>
      <div className="rounded border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-800">
        <span className="font-semibold">Important:</span> Your PIN is single-use. Once entered,
        you will be taken directly to the signing form — please complete signing without
        navigating away.
      </div>

      {!pinSent ? (
        // Step 1 — the PIN is minted only once the signer asks for it here,
        // so it can't sit in an inbox before anyone opened the document.
        <div className="mt-6 text-center">
          <h1 className="text-base font-semibold text-ink-primary">
            Verify it&apos;s you
          </h1>
          <p className="mx-auto mt-1 max-w-sm text-xs text-ink-muted">
            We&apos;ll email you a single-use 6-digit PIN. Enter it here to open your
            Delegation of Authority for signing.
          </p>
          <button
            type="button"
            onClick={requestPin}
            disabled={sendingPin || !rowId}
            className="mt-5 rounded bg-accent-600 px-5 py-2 text-sm font-medium text-white hover:bg-accent-700 disabled:opacity-50"
          >
            {sendingPin ? 'Sending…' : 'Send PIN to my email'}
          </button>
          {error && <div className="mt-3 text-xs text-red-600">{error}</div>}
        </div>
      ) : (
        <>
      <h1 className="mt-5 text-center text-base font-semibold text-ink-primary">
        Enter your signing PIN
      </h1>
      <p className="mt-1 text-center text-xs text-ink-muted">
        {pinDestination
          ? `We sent a 6-digit PIN to ${pinDestination}`
          : 'Enter the 6-digit PIN we just emailed you'}
      </p>

      <div className="mt-6 flex justify-center gap-1.5">
        {digits.map((d, i) => (
          <input
            key={i}
            id={`pin-${i}`}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={d}
            disabled={submitting}
            onChange={(e) => {
              const v = e.target.value.replace(/\D/g, '').slice(-1);
              setDigits((prev) => {
                const next = [...prev];
                next[i] = v;
                return next;
              });
              if (v && i < PIN_LEN - 1) {
                (document.getElementById(`pin-${i + 1}`) as HTMLInputElement | null)?.focus();
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Backspace' && !digits[i] && i > 0) {
                (document.getElementById(`pin-${i - 1}`) as HTMLInputElement | null)?.focus();
              }
            }}
            onPaste={(e) => {
              const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, PIN_LEN);
              if (pasted) {
                e.preventDefault();
                const next = Array(PIN_LEN).fill('');
                for (let j = 0; j < pasted.length; j++) next[j] = pasted[j];
                setDigits(next);
                const focusIdx = Math.min(pasted.length, PIN_LEN - 1);
                (document.getElementById(`pin-${focusIdx}`) as HTMLInputElement | null)?.focus();
              }
            }}
            className={`h-12 w-10 rounded border text-center text-xl font-semibold ${
              error
                ? 'border-danger-400 text-danger-700'
                : 'border-surface-border text-ink-primary'
            } focus:border-accent-500 focus:outline-none`}
          />
        ))}
      </div>

      {error && <p className="mt-3 text-center text-xs text-danger-600">{error}</p>}

      <button
        type="button"
        onClick={submit}
        disabled={!ready || submitting}
        className="mt-6 w-full rounded bg-accent-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-accent-700 disabled:opacity-50"
      >
        {submitting ? 'Verifying…' : 'Start signing'}
      </button>

      <p className="mt-6 text-center text-[11px] text-ink-muted">
        Didn&apos;t get it?{' '}
        <button
          type="button"
          onClick={requestPin}
          disabled={sendingPin}
          className="underline hover:text-ink-primary disabled:opacity-50"
        >
          {sendingPin ? 'Sending…' : 'Send another PIN'}
        </button>
      </p>
        </>
      )}
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-surface-base">
      <div className="mx-auto max-w-md px-4 py-10">
        <div className="mb-6 flex items-center justify-center gap-2">
          <ClipboardSignature className="h-5 w-5 text-accent-600" />
          <div>
            <div className="text-base font-semibold text-ink-primary">DreamTrials</div>
            <div className="text-[10px] uppercase tracking-wider text-ink-muted">
              Delegation of Authority
            </div>
          </div>
        </div>
        <div className="rounded border border-surface-border bg-surface-card p-6 shadow-sm">
          <div className="mb-5 flex justify-center">
            <ShieldCheck className="h-8 w-8 text-accent-600" />
          </div>
          {children}
        </div>
      </div>
    </div>
  );
}
